import { Link } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { useCart } from '../state/cart';
import { useCustomerAuth } from '../state/customerAuth';
import southAsiaWorld from '../data/world.southAsia.json';
import { submitOrder } from '../api/catalog';

function buildOrderLines(items) {
  return items.map((it) => {
    const id = String(it.id ?? '');
    const qty = Math.max(1, Number(it.qty ?? 1));
    const unit_price = Number(it.price ?? 0);
    if (id.startsWith('product:')) {
      const product_id = parseInt(id.slice('product:'.length), 10);
      if (!Number.isFinite(product_id) || product_id < 1) {
        throw new Error('Invalid cart item (missing product id).');
      }
      return { product_id, quantity: qty, unit_price };
    }
    if (id.startsWith('shop:')) {
      const slug = id.slice('shop:'.length);
      if (!slug) throw new Error('Invalid cart item (missing slug).');
      return { slug, quantity: qty, unit_price };
    }
    throw new Error('Cart contains items that cannot be submitted. Re-add items from the shop.');
  });
}

export function CheckoutPage() {
  const { items, subtotal, clear } = useCart();
  const { token, customer, isAuthenticated } = useCustomerAuth();
  const countries = useMemo(() => southAsiaWorld?.countries || [], []);

  const [details, setDetails] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address1: '',
    address2: '',
    countryCode: '',
    stateCode: '',
    city: '',
    postalCode: '',
    notes: '',
  });
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [orderResult, setOrderResult] = useState(null);

  const selectedCountry = useMemo(() => {
    return countries.find((c) => c.code === details.countryCode) || null;
  }, [countries, details.countryCode]);

  const states = useMemo(() => selectedCountry?.states || [], [selectedCountry]);
  const selectedState = useMemo(() => {
    return states.find((s) => s.code === details.stateCode) || null;
  }, [states, details.stateCode]);

  const cities = selectedState?.cities || [];

  useEffect(() => {
    if (!customer) return;
    const parts = String(customer.name || '').trim().split(/\s+/).filter(Boolean);
    const firstName = parts[0] || '';
    const lastName = parts.length > 1 ? parts.slice(1).join(' ') : '';
    setDetails((d) => ({
      ...d,
      firstName: d.firstName || firstName,
      lastName: d.lastName || lastName,
      email: d.email || customer.email || '',
      phone: d.phone || customer.phone || '',
      address1: d.address1 || customer.address_line1 || '',
      address2: d.address2 || customer.address_line2 || '',
      countryCode: d.countryCode || customer.country || '',
      city: d.city || customer.city || '',
      postalCode: d.postalCode || customer.postal_code || '',
    }));
  }, [customer]);

  function update(patch) {
    setDetails((d) => ({ ...d, ...patch }));
  }

  function validate() {
    const required = [
      ['firstName', 'First name'],
      ['lastName', 'Last name'],
      ['email', 'Email'],
      ['phone', 'Phone'],
      ['address1', 'Address line 1'],
      ['countryCode', 'Country'],
      ['stateCode', 'State/Province'],
      ['city', 'City'],
      ['postalCode', 'Postal code'],
    ];
    for (const [key, label] of required) {
      if (!String(details[key] || '').trim()) return `${label} is required.`;
    }
    if (!/^\S+@\S+\.\S+$/.test(details.email.trim())) return 'Enter a valid email.';
    return '';
  }

  async function handlePlaceOrder() {
    const err = validate();
    if (err) {
      setSubmitError(err);
      return;
    }
    setSubmitError('');
    setSubmitting(true);
    setOrderResult(null);
    try {
      const lines = buildOrderLines(items);
      const stateLabel = (selectedState?.name || details.stateCode || '').trim();
      const data = await submitOrder(
        {
          customer: {
            first_name: details.firstName.trim(),
            last_name: details.lastName.trim(),
            email: details.email.trim(),
            phone: details.phone.trim(),
            address_line1: details.address1.trim(),
            address_line2: details.address2.trim() || null,
            city: details.city.trim(),
            state: stateLabel || null,
            postal_code: details.postalCode.trim(),
            country: String(details.countryCode).trim().toUpperCase(),
          },
          lines,
          notes: details.notes.trim() || null,
        },
        token || undefined,
      );
      setOrderResult(data);
      clear();
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : 'Order could not be placed.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="section-padding elenardSectionTight">
      <div className="container">
        <div className="sec-head mb-30">
          <span className="sub-title main-color mb-10">Checkout</span>
          <h3 className="fw-600 fz-50 text-u">
            Secure <span className="fw-200">checkout</span>
          </h3>
          <div className="opacity-8 fz-14 mt-10">
            Review your order and place it. Payment (Stripe) can be wired next.
          </div>
          {isAuthenticated && customer ? (
            <div className="fz-14 mt-15" style={{ opacity: 0.9 }}>
              Signed in as <strong>{customer.email}</strong> — this order will appear in{' '}
              <Link to="/account/dashboard">your account</Link> (checkout email must match).
            </div>
          ) : null}
        </div>

        {orderResult ? (
          <div className="item" style={{ padding: 22 }}>
            <h5 className="mb-15">Order placed</h5>
            <p className="mb-10">
              Thank you! Your order <strong>{String(orderResult.number || orderResult.order_id)}</strong>{' '}
              was received.
            </p>
            <p className="opacity-8 mb-20">
              Total: <strong>{orderResult.total_formatted || `$${Number(orderResult.total).toFixed(2)}`}</strong>
            </p>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <Link to="/account/dashboard" className="butn butn-md butn-bord radius-30">
                <span className="text">View my orders</span>
              </Link>
              <Link to="/shop" className="butn butn-md butn-bg main-colorbg radius-30">
                <span className="text">Continue shopping</span>
              </Link>
            </div>
          </div>
        ) : items.length === 0 ? (
          <div className="item" style={{ padding: 18 }}>
            <h5 className="mb-10">Your cart is empty.</h5>
            <p className="opacity-8 mb-20">Add items before checking out.</p>
            <Link to="/shop" className="butn butn-md butn-bg main-colorbg radius-30">
              <span className="text">Return to shop</span>
            </Link>
          </div>
        ) : (
          <div className="elenardCartGrid">
            <form
              className="elenardCartItems item"
              onSubmit={(e) => {
                e.preventDefault();
                handlePlaceOrder();
              }}
            >
              <div className="sec-head mb-20">
                <span className="sub-title main-color mb-10">Customer details</span>
              </div>

              <div className="elenardFormGrid">
                <div className="elenardFormField">
                  <label htmlFor="firstName">First name *</label>
                  <input
                    id="firstName"
                    className="elenardShopInput"
                    value={details.firstName}
                    onChange={(e) => update({ firstName: e.target.value })}
                    required
                  />
                </div>
                <div className="elenardFormField">
                  <label htmlFor="lastName">Last name *</label>
                  <input
                    id="lastName"
                    className="elenardShopInput"
                    value={details.lastName}
                    onChange={(e) => update({ lastName: e.target.value })}
                    required
                  />
                </div>
                <div className="elenardFormField">
                  <label htmlFor="email">Email *</label>
                  <input
                    id="email"
                    type="email"
                    className="elenardShopInput"
                    value={details.email}
                    onChange={(e) => update({ email: e.target.value })}
                    required
                    readOnly={isAuthenticated}
                    title={isAuthenticated ? 'Must match your account email when signed in' : undefined}
                  />
                  {isAuthenticated ? (
                    <div className="elenardFormHint">Locked to your account email while signed in.</div>
                  ) : null}
                </div>
                <div className="elenardFormField">
                  <label htmlFor="phone">Phone *</label>
                  <input
                    id="phone"
                    className="elenardShopInput"
                    value={details.phone}
                    onChange={(e) => update({ phone: e.target.value })}
                    placeholder="+91..."
                    required
                  />
                </div>
              </div>

              <div className="sec-head mb-20 mt-30">
                <span className="sub-title main-color mb-10">Shipping address</span>
              </div>

              <div className="elenardFormGrid">
                <div className="elenardFormField" style={{ gridColumn: '1 / -1' }}>
                  <label htmlFor="address1">Address line 1 *</label>
                  <input
                    id="address1"
                    className="elenardShopInput"
                    value={details.address1}
                    onChange={(e) => update({ address1: e.target.value })}
                    required
                  />
                </div>
                <div className="elenardFormField" style={{ gridColumn: '1 / -1' }}>
                  <label htmlFor="address2">Address line 2</label>
                  <input
                    id="address2"
                    className="elenardShopInput"
                    value={details.address2}
                    onChange={(e) => update({ address2: e.target.value })}
                  />
                </div>

                <div className="elenardFormField">
                  <label htmlFor="country">Country *</label>
                  <select
                    id="country"
                    className="elenardShopSelect"
                    value={details.countryCode}
                    onChange={(e) =>
                      update({
                        countryCode: e.target.value,
                        stateCode: '',
                        city: '',
                      })
                    }
                    required
                  >
                    <option value="">Select country</option>
                    {countries.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="elenardFormField">
                  <label htmlFor="state">State/Province *</label>
                  <select
                    id="state"
                    className="elenardShopSelect"
                    value={details.stateCode}
                    onChange={(e) => update({ stateCode: e.target.value, city: '' })}
                    required
                    disabled={!details.countryCode}
                  >
                    <option value="">Select state</option>
                    {states.map((s) => (
                      <option key={s.code} value={s.code}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="elenardFormField">
                  <label htmlFor="city">City *</label>
                  <select
                    id="city"
                    className="elenardShopSelect"
                    value={details.city}
                    onChange={(e) => update({ city: e.target.value })}
                    required
                    disabled={!details.stateCode}
                  >
                    <option value="">Select city</option>
                    {cities.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="elenardFormField">
                  <label htmlFor="postalCode">Postal code *</label>
                  <input
                    id="postalCode"
                    className="elenardShopInput"
                    value={details.postalCode}
                    onChange={(e) => update({ postalCode: e.target.value })}
                    required
                  />
                </div>

                <div className="elenardFormField" style={{ gridColumn: '1 / -1' }}>
                  <label htmlFor="notes">Order notes</label>
                  <input
                    id="notes"
                    className="elenardShopInput"
                    value={details.notes}
                    onChange={(e) => update({ notes: e.target.value })}
                    placeholder="Any delivery instructions?"
                  />
                  <div className="elenardFormHint">
                    Orders are sent to your Laravel API (<code>POST /api/v1/orders</code>) when you place the
                    order.
                  </div>
                </div>
              </div>

              {submitError ? <div className="elenardFormError">{submitError}</div> : null}

              <div className="mt-30" style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button
                  className="butn butn-md butn-bg main-colorbg radius-30"
                  type="submit"
                  disabled={submitting}
                >
                  <span className="text">{submitting ? 'Placing order…' : 'Place order'}</span>
                </button>
                <Link to="/cart" className="butn butn-md butn-bord radius-30">
                  <span className="text">Back to cart</span>
                </Link>
              </div>
            </form>

            <div className="elenardCartSummary item">
              <div className="elenardCartSummaryInner">
                <h5 className="mb-10">Order summary</h5>
                <div className="elenardCartSummaryRow">
                  <span className="opacity-8">Subtotal</span>
                  <span className="fw-600">${subtotal}</span>
                </div>
                <div className="elenardCartSummaryRow">
                  <span className="opacity-8">Shipping</span>
                  <span className="opacity-8">Calculated at payment</span>
                </div>
                <div className="elenardCartSummaryDivider" />
                <div className="elenardCartSummaryRow">
                  <span className="fw-600">Total</span>
                  <span className="fw-700">${subtotal}</span>
                </div>

                <div className="mt-20" style={{ display: 'grid', gap: 10 }}>
                  <button
                    className="butn butn-md butn-bg main-colorbg radius-30"
                    type="button"
                    disabled={submitting}
                    onClick={() => handlePlaceOrder()}
                  >
                    <span className="text">{submitting ? 'Placing order…' : 'Place order'}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
